/**
 * Company Service - API calls for multi-tenant company management
 *
 * Endpoints:
 * - GET  /api/companies/profile       - Get company details
 * - GET  /api/companies/subscription  - Get subscription info
 * - GET  /api/companies/stats         - Get company statistics
 * - GET  /api/companies/users         - List all company users (Admin only)
 * - PUT  /api/companies/profile       - Update company profile (Admin only)
 * - PUT  /api/companies/users/{id}/role - Update user role (Admin only)
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const companyService = {

  // ==================== GET COMPANY PROFILE ====================
  /**
   * Get company profile details
   * Returns: company name, domain, logo, subscription tier, etc.
   */
  getProfile: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('[companyService] Fetching company profile...');

      const response = await fetch(`${API_BASE_URL}/companies/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get company profile');
      }

      console.log('[companyService] Company profile loaded:', data);
      return data;
    } catch (error) {
      console.error('[companyService] Error fetching profile:', error);
      throw error;
    }
  },

  // ==================== GET SUBSCRIPTION INFO ====================
  /**
   * Get company subscription details
   * Returns: subscription tier, limits, usage, expiry date
   */
  getSubscription: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('[companyService] Fetching subscription info...');

      const response = await fetch(`${API_BASE_URL}/companies/subscription`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get subscription info');
      }

      console.log('[companyService] Subscription info loaded:', data);
      return data;
    } catch (error) {
      console.error('[companyService] Error fetching subscription:', error);
      throw error;
    }
  },

  // ==================== GET COMPANY STATS ====================
  /**
   * Get company statistics
   * Returns: total users, active users, total jobs, total candidates, etc.
   */
  getStats: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('[companyService] Fetching company stats...');

      const response = await fetch(`${API_BASE_URL}/companies/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get company stats');
      }

      console.log('[companyService] Company stats loaded:', data);
      return data;
    } catch (error) {
      console.error('[companyService] Error fetching stats:', error);
      throw error;
    }
  },

  // ==================== GET COMPANY USERS (ADMIN ONLY) ====================
  /**
   * Get list of all users in the company
   * Admin only - returns all users with their roles
   */
  getUsers: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('[companyService] Fetching company users...');

      const response = await fetch(`${API_BASE_URL}/companies/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get company users');
      }

      console.log('[companyService] Company users loaded:', data);
      return data;
    } catch (error) {
      console.error('[companyService] Error fetching users:', error);
      throw error;
    }
  },

  // ==================== UPDATE COMPANY PROFILE (ADMIN ONLY) ====================
  /**
   * Update company profile
   * Admin only - update company name, logo, settings
   */
  updateProfile: async (profileData) => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('[companyService] Updating company profile...');

      const response = await fetch(`${API_BASE_URL}/companies/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update company profile');
      }

      console.log('[companyService] Company profile updated:', data);
      return data;
    } catch (error) {
      console.error('[companyService] Error updating profile:', error);
      throw error;
    }
  },

  // ==================== UPDATE USER ROLE (ADMIN ONLY) ====================
  /**
   * Update a user's role
   * Admin only - change user between USER and ADMIN roles
   *
   * @param {number} userId - The user ID to update
   * @param {string} newRole - The new role: "USER" or "ADMIN"
   */
  updateUserRole: async (userId, newRole) => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log(`[companyService] Updating user ${userId} role to ${newRole}...`);

      const response = await fetch(`${API_BASE_URL}/companies/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user role');
      }

      console.log('[companyService] User role updated:', data);
      return data;
    } catch (error) {
      console.error('[companyService] Error updating user role:', error);
      throw error;
    }
  },

  // ==================== HELPER FUNCTIONS ====================

  /**
   * Format subscription tier for display
   */
  formatSubscriptionTier: (tier) => {
    const tiers = {
      'FREE': { name: 'Free', color: '#6b7280', icon: '🆓' },
      'BASIC': { name: 'Basic', color: '#3b82f6', icon: '📦' },
      'PROFESSIONAL': { name: 'Professional', color: '#8b5cf6', icon: '💼' },
      'ENTERPRISE': { name: 'Enterprise', color: '#059669', icon: '🏢' },
    };
    return tiers[tier] || { name: tier, color: '#6b7280', icon: '📋' };
  },

  /**
   * Format role for display
   */
  formatRole: (role) => {
    const roles = {
      'USER': { name: 'User', color: '#3b82f6', icon: '👤' },
      'ADMIN': { name: 'Admin', color: '#059669', icon: '⚙️' },
      'COMPANY_ADMIN': { name: 'Admin', color: '#059669', icon: '⚙️' },
    };
    return roles[role] || { name: role, color: '#6b7280', icon: '❓' };
  },

  /**
   * Check if user limit is reached
   */
  isUserLimitReached: (subscription) => {
    if (!subscription || !subscription.limits) return false;

    const { currentUsers, maxUsers } = subscription.limits;
    return maxUsers !== -1 && currentUsers >= maxUsers;
  },

  /**
   * Check if job limit is reached
   */
  isJobLimitReached: (subscription) => {
    if (!subscription || !subscription.limits) return false;

    const { currentJobs, maxJobs } = subscription.limits;
    return maxJobs !== -1 && currentJobs >= maxJobs;
  },

  /**
   * Calculate usage percentage
   */
  getUsagePercentage: (current, max) => {
    if (max === -1) return 0; // Unlimited
    if (max === 0) return 100;
    return Math.round((current / max) * 100);
  },

};

export default companyService;